import { storage } from '@/lib/storage';
import { env } from '@/env';
import { HttpError } from '@/lib/api/error';
import {
  addressContract,
  adminSyncContract,
  analyticsContract,
  authContract,
  billingContract,
  catalogAdminContract,
  catalogCheckoutContract,
  checkoutContract,
  consumptionAlertContract,
  dashboardContract,
  identityContract,
  invoiceContract,
  notificationSettingsContract,
  operatorContract,
  orderContract,
  organizationContract,
  organizationMembersContract,
  phonePlanContract,
  simCardContract,
  telephonyContract,
  siteContract,
  travelEsimContract,
} from '@octave/contracts';
import {
  initClient,
  tsRestFetchApi,
  type ApiFetcherArgs,
  type AppRouter,
} from '@ts-rest/core';
import {
  isPreAuthPath,
  logoutToLogin,
  refreshTokens,
} from '@/lib/auth/refresh';

function withAuthHeaders(
  headers: Record<string, string>,
): Record<string, string> {
  const result = { ...headers };
  const token = storage.get('accessToken');
  if (token) result.authorization = `Bearer ${token}`;
  const tenantSlug = storage.get('tenantSlug');
  if (tenantSlug) result['x-tenant-slug'] = tenantSlug;
  return result;
}

/**
 * Nest sends an EMPTY body (no content-type) when a handler returns `null`
 * (`isNil` check in the Express adapter) — the fetch client would surface it
 * as a truthy empty Blob and skip response validation, so `body?.items`-style
 * guards let it through (prod crash on `getCheckout` with no open checkout).
 * Normalize it back to the JSON `null` the contract declares.
 */
export function normalizeNullBody<
  T extends { status: number; body: unknown },
>(result: T): T {
  if (result.body instanceof Blob && result.body.size === 0) {
    return { ...result, body: null };
  }
  return result;
}

/**
 * ts-rest client factory — `validateResponse: true` parses every response
 * body against the contract's zod schema: a drifting backend fails loudly
 * instead of corrupting the UI. A 401 triggers one silent token refresh and
 * replay; if the refresh fails, the session is over and the user is sent
 * back to login.
 */
function createTsRestClient<T extends AppRouter>(contract: T) {
  return initClient(contract, {
    baseUrl: env.VITE_API_URL,
    validateResponse: true,
    api: async (args: ApiFetcherArgs) => {
      const result = normalizeNullBody(
        await tsRestFetchApi({
          ...args,
          headers: withAuthHeaders(args.headers),
        }),
      );
      if (result.status !== 401 || isPreAuthPath(args.route.path)) {
        return result;
      }
      if (!(await refreshTokens())) {
        logoutToLogin();
        return result;
      }
      return normalizeNullBody(
        await tsRestFetchApi({
          ...args,
          headers: withAuthHeaders(args.headers),
        }),
      );
    },
  });
}

/**
 * Build an HttpError from a non-2xx ts-rest response, preserving the
 * backend's `message` (Nest exception shape) when the body carries one and
 * the status for callers that branch on it (e.g. `use404Redirect`).
 */
export function apiError(
  res: { status: number; body: unknown },
  fallback: string,
): HttpError {
  return new HttpError(backendMessage(res.body) ?? fallback, res.status);
}

function backendMessage(body: unknown): string | null {
  if (
    typeof body === 'object' &&
    body !== null &&
    'message' in body &&
    typeof (body as { message: unknown }).message === 'string'
  ) {
    return (body as { message: string }).message;
  }
  return null;
}

export const addressClient = createTsRestClient(addressContract);
export const analyticsClient = createTsRestClient(analyticsContract);
export const authClient = createTsRestClient(authContract);
export const adminSyncClient = createTsRestClient(adminSyncContract);
export const billingClient = createTsRestClient(billingContract);
export const catalogAdminClient = createTsRestClient(catalogAdminContract);
export const catalogCheckoutClient = createTsRestClient(
  catalogCheckoutContract,
);
export const checkoutClient = createTsRestClient(checkoutContract);
export const consumptionAlertClient = createTsRestClient(
  consumptionAlertContract,
);
export const phonePlanClient = createTsRestClient(phonePlanContract);
export const dashboardClient = createTsRestClient(dashboardContract);
export const identityClient = createTsRestClient(identityContract);
export const invoiceClient = createTsRestClient(invoiceContract);
export const notificationSettingsClient = createTsRestClient(
  notificationSettingsContract,
);
export const operatorClient = createTsRestClient(operatorContract);
export const orderClient = createTsRestClient(orderContract);
export const organizationClient = createTsRestClient(organizationContract);
export const orgMembersClient = createTsRestClient(
  organizationMembersContract,
);
export const simCardClient = createTsRestClient(simCardContract);
export const lineClient = createTsRestClient(telephonyContract);
export const siteClient = createTsRestClient(siteContract);
export const travelEsimClient = createTsRestClient(travelEsimContract);
